/**
 * Authentication schemas
 */
import { z } from "zod";
import { ReferrerTypeSchema, StrapiEntitySchema, StrapiIdSchema } from "./common.schemas";
import { UserSchema } from "./user.schemas";
// ============================================================================
// Input Schemas
// ============================================================================
/**
 * Login input schema
 */
export const LoginInputSchema = z
    .object({
    identifier: z.string().min(1, "Email or username is required"),
    password: z.string().min(1, "Password is required"),
})
    .strict();
/**
 * Register input schema
 * Matches: apps/api/src/extensions/users-permissions/middlewares/register-custom-validation.ts
 */
export const RegisterInputSchema = z
    .object({
    email: z.string().email("Invalid email address"),
    password: z.string().min(8, "Password must be at least 8 characters"),
    name: z.string().optional(),
    country: z.string().optional(),
    referralCode: z.string().optional(),
    referrerCode: z.string().optional(),
    referrerType: ReferrerTypeSchema.optional(),
    language: z.enum(["en", "es"]).optional(),
})
    .strict();
/**
 * Forgot password input schema
 */
export const ForgotPasswordInputSchema = z
    .object({
    email: z.string().email("Invalid email address"),
})
    .strict();
/**
 * Reset password input schema
 */
export const ResetPasswordInputSchema = z
    .object({
    code: z.string().min(1, "Reset code is required"),
    password: z.string().min(8, "Password must be at least 8 characters"),
    passwordConfirmation: z.string().min(1, "Password confirmation is required"),
})
    .strict()
    .refine((data) => data.password === data.passwordConfirmation, {
    message: "Passwords do not match",
    path: ["passwordConfirmation"],
});
/**
 * Change password input schema
 */
export const ChangePasswordInputSchema = z
    .object({
    currentPassword: z.string().min(1, "Current password is required"),
    password: z.string().min(8, "New password must be at least 8 characters"),
    passwordConfirmation: z.string().min(1, "Password confirmation is required"),
})
    .strict()
    .refine((data) => data.password === data.passwordConfirmation, {
    message: "Passwords do not match",
    path: ["passwordConfirmation"],
});
/**
 * Impersonate user input schema
 */
export const ImpersonateUserInputSchema = z
    .object({
    userId: z.number().int().positive("User ID must be a positive integer"),
})
    .strict();
// ============================================================================
// Entity Schemas
// ============================================================================
/**
 * User role schema
 */
export const UserRoleSchema = StrapiEntitySchema.extend({
    name: z.string(),
    description: z.string().optional(),
    type: z.string(),
});
/**
 * User permission schema
 */
export const UserPermissionSchema = StrapiEntitySchema.extend({
    action: z.string(),
    enabled: z.boolean(),
    role: StrapiIdSchema.optional(),
});
/**
 * Locale schema
 */
export const LocaleSchema = z.object({
    id: StrapiIdSchema,
    name: z.string(),
    code: z.string(),
    isDefault: z.boolean(),
});
/**
 * Login response schema (User defined externally to avoid circular deps)
 */
export const LoginResponseSchema = z.object({
    jwt: z.string(),
    user: UserSchema,
});
/**
 * Renew token response schema
 * Only returns a new JWT (the caller uses the stored user from the existing session)
 */
export const RenewTokenResponseSchema = z.object({
    jwt: z.string(),
});
/**
 * Register response schema
 */
export const RegisterResponseSchema = z.object({
    jwt: z.string(),
    user: UserSchema,
});
/**
 * Impersonate response schema
 * Only returns a new JWT (the caller fetches user details separately)
 */
export const ImpersonateResponseSchema = z.object({
    jwt: z.string(),
});
